import React, { useEffect, useState, useRef } from 'react';
import { Alert, Grid, Button, Paper, Typography, CircularProgress, Stack} from '@mui/material';
import AssistantIcon from '@mui/icons-material/Assistant';

//components imports
import AlertInfo from '../../../dataObjects/AlertInfo';
import FormGeneralInfo from './FormGeneralInfoComponent';
import ChangeTreasurerForm from './ChangeTreasurerFormComponent';
import PdfObject from '../dataObjects/PdfObject';
import BankAppointmentForm from './BankAppointmentFormComponent';
import { GenericFormObject } from '../dataObjects/GenericFormObject';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import ChangePurposeForm from './ChangePurposeFormComponent';
import OrganizationForm from './OrganizationFormComponent';
import ShortFilerObject from '../dataObjects/ShortFilerObject';
import DisplayJson from './DisplayJsonComponent';


const SupportedFormCollections = ['T101', 'P101', 'D103', '101', '101 PC', '101 BQ', '101 IEPAC', '101 SFA', '101 WTC'];

interface FormDataProps {
    selectedForm: PdfObject;
    closeGenericForm: () => void;
}

const GenericForm = (props: FormDataProps): JSX.Element => {

    //set up state
    const [formType, setFormType] = useState('other');
    const [accountTypeCode, setAccountTypeCode] = useState<string>('D');
    const [formData, setFormData] = useState<GenericFormObject | null>(null);
    const [filerData, setFilerData] = useState<ShortFilerObject | null>(null);
    const [isFormProcessed, setIsFormProcessed] = useState<boolean>(false);

    // a timer to show how long it would take to load the form data
    const [timer, setTimer] = useState<number>(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const getProcessingMessage = (): string => {
        const dots = '.'.repeat((timer % 2) + 1);
        if (timer < 4) return `Reading form title${dots}`;
        if (timer < 8) return `Retrieving form type${dots}`;
        if (timer < 12) return `Reading committee info${dots}`;
        if (timer < 16) return `Reading contact info${dots}`;
        if (timer < 20) return `Reading main content${dots}`;
        return `Keep reading main content${dots}`;
    }

    const isSupportedForm = (formType: string): boolean => {
        return SupportedFormCollections.includes(formType);
    }

    useEffect(() => {
        retrieveFormData();
        //to be removed later
        //resetFormType(formType);
    }, [props.selectedForm]);

    // Start timer when loading begins, stop when loading ends
    useEffect(() => {
        setTimer(0);
        if (!isFormProcessed) {
            timerRef.current = setInterval(() => {
                setTimer(prev => prev + 1);
            }, 1000);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [props.selectedForm, isFormProcessed]);

    useEffect(() => {
        if (isFormProcessed && timerRef.current) {
            clearInterval(timerRef.current);
        }
    }, [isFormProcessed]);

    //need to get the processed form json data from endpoint
    const retrieveFormData = async () => {

        await fetch(`/LLMFormProcessor/GetFormData?pdfUri=${props.selectedForm.Uri}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetFormData',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    /*
                    // Step 1: Parse outer JSON
                    const parsedResponse = JSON.parse(result.result);
                    // Step 2: Extract content
                    const content = parsedResponse.content;
                    // Step 3: Extract JSON inside ```json ... ```
                    const match = content.match(/```json\s*(\{.*\})\s*```/s);
                    if (!match) {
                        throw new Error("JSON block not found");
                    }
                    const jsonOnly = match[1];

                    // Step 4: Parse inner JSON
                    const formData = JSON.parse(jsonOnly);
                    */
                    let formData = result.result.formData;
                    formData.isReopen = false;
                    formData.ocpfCommitteeName = '';
                    formData.formIntakeBlobUrl = props.selectedForm.Uri;
                    setFormData(formData);

                    if (result.result.formData.formType) {
                        resetFormType(result.result.formData.formType);
                    }
                    else {
                        setFormType('other');
                    }
                    
                    setFilerData(result.result.filer);
                    setIsFormProcessed(true);
                }
            }
        )
    }

    //update filer CPF ID
    const updateFilerCpfId = (newCpfId: string, name:string) => {
        setFormData(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                cpfID: newCpfId,
                isReopen: true,
                ocpfCommitteeName: name,
            };
        });
    }

    // need to render different form components based on form type. can be done by conditional rendering
    const renderFormComponent = () => {
        switch (formType) {
            case 'T101':
                return formData ? <ChangeTreasurerForm formData={formData} closeGenericForm={props.closeGenericForm}></ChangeTreasurerForm>: <></>
            case 'D103':
                return formData ? <BankAppointmentForm formData={formData} closeGenericForm={props.closeGenericForm}></BankAppointmentForm> : <></>
            case 'P101':
                return formData ? <ChangePurposeForm formData={formData} closeGenericForm={props.closeGenericForm}></ChangePurposeForm> : <></>
            case 'CPF 101':
            case '101':
            case '101 PC':
            case '101 BQ':
            case '101 IEPAC':
            case '101 SFA':
            case '101 WTC':
                return formData ? <OrganizationForm formData={formData} closeGenericForm={props.closeGenericForm} formType={formType} accounTypeCode={accountTypeCode}></OrganizationForm> : <></>
            default:
                return <></>
        }
    }

    const resetFormType = (type: string) => {

        if (!type) return;

        if (type === '101') type = 'CPF 101';

        if (type.toLowerCase().includes('t101')) {
            setFormType('T101');
            return;
        };

        if (type.toLowerCase().includes('d103')) {
            setFormType('D103');
            return;
        };

        if (type.toLowerCase().includes('p101')) {
            setFormType('P101');
            return;
        };

        if (type.toLowerCase().includes('purpose') || type.toLowerCase().includes('101 p')) {
            setFormType('P101');
            return;
        }

        if (type.toLowerCase().includes('101 pc')) {
            setFormType('101 PC');
            setAccountTypeCode('P');
            return;
        };

        if (type.toLowerCase().includes('101 bq')) {
            setFormType('101 BQ');
            setAccountTypeCode('X');
            return;
        };

        if (type.toLowerCase().includes('101-bq')) {
            setFormType('101 BQ');
            setAccountTypeCode('X');
            return;
        };

        if (type.toLowerCase().includes('101 iepac')) {
            setFormType('101 IEPAC');
            setAccountTypeCode('I');
            return;
        };

        if (type.toLowerCase().includes('101 wtc')) {
            setFormType('101 WTC');
            setAccountTypeCode('L');
            return;
        };

        if (type.toLowerCase().includes('101 sfa')) {
            setFormType('101 SFA');
            setAccountTypeCode('V');
            return;
        };

        if (type.toLowerCase().includes('cpf 101') || type.toLowerCase().includes('statement of organization')) {
            setFormType('101');
            return;
        };

        setFormType('other');
    }

    const renderFormType = () => {
        switch (formType) {
            case 'T101':
                return 'Form CPF T101: Change of Treasurer';
            case 'P101':
                return 'Form CPF 101 P: Change of Purpose';
            case 'D103':
                return 'Form CPF D103: Appointment of Depository Bank';
            case 'CPF 101':
            case '101':
                return 'Form CPF 101: Statement of Organization Candidate Committee';
            case '101 PC':
                return 'Form CPF 101: Statement of Organization Political Action Committee';
            case '101 IEPAC':
                return 'Form CPF 101: Statement of Organization Independent Expenditure Political Action Committee';
            case '101 WTC':
                return 'Form CPF 101: Statement of Organization Local Party Committee';
            case '101 BQ':
                return 'Form CPF 101: Statement of Organization Ballot Question Committee';
            default:
                return '';
        }
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
        props.closeGenericForm();
    }

    return (
        <>
            <Grid item xs={10} display="flex" justifyContent="flex-end">
                <Button size="small" variant="outlined" onClick={() => props.closeGenericForm()} >Return to PDF List</Button>
            </Grid>
            {
                !isFormProcessed &&
                <Grid item xs={12} display="flex" justifyContent="center">
                    <Alert severity="warning" icon={false}>
                        <Stack direction="row" spacing={1}>
                            <CircularProgress size={18} />
                            <Typography variant="body1">
                                    OCPF AI Assistant is working hard to process the following form:
                            </Typography>
                        </Stack>
                        <Stack marginTop={2}>
                            <Typography fontStyle='italic' textAlign='center'>
                                    {getProcessingMessage()}
                            </Typography>
                        </Stack>
                    </Alert>
                </Grid>
            }
            {
                isFormProcessed && isSupportedForm(formType) &&
                <>
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <Alert severity="success" icon={<AssistantIcon fontSize="inherit" />} >The selected form has been successfully processed by the OCPF AI assistant. Please review the information below carefully, then click <b>Save</b> to proceed.</Alert>
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="center" marginBottom={2} >
                        <Typography variant="h6" fontWeight={550} > {renderFormType()}</Typography>
                    </Grid>
                    <Grid item xs={12} md={5.5} height={1}>
                        <Grid item xs={12}>
                            {
                                formData &&
                                <FormGeneralInfo formData={formData} formType={formType} accountTypeCode={accountTypeCode} updateCpfId={updateFilerCpfId} filerData={filerData} ></FormGeneralInfo>
                            }
                            
                        </Grid>
                        {/*Todo: conditional rending*/}
                        <Grid item xs={12} marginTop={2} >
                            {   formData
                                && renderFormComponent()
                            }
                        </Grid>
                    </Grid>
                </>
            }
            {
                isFormProcessed && !isSupportedForm(formType) &&
                <>
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <Alert severity="warning" icon={<AssistantIcon fontSize="inherit" />} >The selected document was successfully processed by the OCPF AI Assistant, but this document type is not currently supported for form processing. The extracted raw JSON data is provided below.</Alert>
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="center" marginBottom={2} >
                        <Typography variant="h6" fontWeight={550} > {formData?.formType}</Typography>
                    </Grid>
                    <Grid item xs={12} md={5.5} height={1}>
                        <Grid item xs={12}>
                            <DisplayJson formData={formData}></DisplayJson>
                        </Grid>
                    </Grid>
                </>
            }
            <Grid item xs={12} md={5} >
                <Paper sx={{ p: 3, position: 'sticky', top: 16 }}>
                    <Grid item xs={12}>
                        {
                            props.selectedForm && props.selectedForm.Uri &&
                            <iframe src={props.selectedForm.Uri} width="100%" height="800px" />
                        }
                    </Grid>
                </Paper>
            </Grid>
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
        </>
    )
}

export default GenericForm;